/**
 * AlertService - Manages console alerts for circuit status, node health, and version drift
 * 
 * Alert Types:
 * - circuit: Circuit status changes (approved, deployed, failed)
 * - node: Node health alerts (degraded, offline)
 * - version: Version drift alerts (nodes running old versions)
 */

const EDGE_URL = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';
const STORAGE_KEY = 'estream-alerts';

export type AlertType = 'circuit' | 'node' | 'version' | 'system';
export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface Alert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  timestamp: number;
  read: boolean;
  dismissed: boolean;
  metadata?: Record<string, unknown>;
}

export interface AlertFilter {
  types?: AlertType[];
  severities?: AlertSeverity[];
  unreadOnly?: boolean;
}

// In-memory alert store
let alerts: Alert[] = [];
let subscribers: Array<(alerts: Alert[]) => void> = [];

// Load alerts from localStorage on init
function loadAlerts(): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      alerts = JSON.parse(stored);
    }
  } catch {
    alerts = [];
  }
}

// Save alerts to localStorage
function saveAlerts(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(alerts));
  } catch {
    // Ignore storage errors
  }
}

// Notify subscribers
function notifySubscribers(): void {
  subscribers.forEach(cb => cb([...alerts]));
}

/**
 * Subscribe to alert updates
 */
export function subscribeToAlerts(callback: (alerts: Alert[]) => void): () => void {
  subscribers.push(callback);
  callback([...alerts]); // Initial call
  return () => {
    subscribers = subscribers.filter(cb => cb !== callback);
  };
}

/**
 * Get all alerts, optionally filtered
 */
export function getAlerts(filter?: AlertFilter): Alert[] {
  let result = alerts.filter(a => !a.dismissed);
  
  if (filter?.types?.length) {
    result = result.filter(a => filter.types!.includes(a.type));
  }
  if (filter?.severities?.length) {
    result = result.filter(a => filter.severities!.includes(a.severity));
  }
  if (filter?.unreadOnly) {
    result = result.filter(a => !a.read);
  }
  
  return result.sort((a, b) => b.timestamp - a.timestamp);
}

/**
 * Get unread alert count
 */
export function getUnreadCount(): number {
  return alerts.filter(a => !a.read && !a.dismissed).length;
}

/**
 * Add a new alert
 */
export function addAlert(alert: Omit<Alert, 'id' | 'timestamp' | 'read' | 'dismissed'>): Alert {
  const newAlert: Alert = {
    ...alert,
    id: `alert-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    timestamp: Date.now(),
    read: false,
    dismissed: false,
  };
  
  alerts = [newAlert, ...alerts].slice(0, 100); // Keep max 100 alerts
  saveAlerts();
  notifySubscribers();
  
  return newAlert;
}

/**
 * Mark an alert as read
 */
export function markAsRead(id: string): void {
  const alert = alerts.find(a => a.id === id);
  if (alert) {
    alert.read = true;
    saveAlerts();
    notifySubscribers();
  }
}

/**
 * Mark all alerts as read
 */
export function markAllAsRead(): void {
  alerts.forEach(a => { a.read = true; });
  saveAlerts();
  notifySubscribers();
}

/**
 * Dismiss an alert
 */
export function dismissAlert(id: string): void {
  const alert = alerts.find(a => a.id === id);
  if (alert) {
    alert.dismissed = true;
    saveAlerts();
    notifySubscribers();
  }
}

/**
 * Clear all alerts
 */
export function clearAlerts(): void {
  alerts = [];
  saveAlerts();
  notifySubscribers();
}

/**
 * Poll for new alerts from the server
 */
export async function pollAlerts(): Promise<void> {
  try {
    const response = await fetch(`${EDGE_URL}/api/alerts`);
    if (!response.ok) return;
    
    const data = await response.json();
    const serverAlerts: Array<{
      id: string;
      type: AlertType;
      severity: AlertSeverity;
      title: string;
      message: string;
      timestamp: number;
      metadata?: Record<string, unknown>;
    }> = data.alerts || [];
    
    // Add new alerts that we don't already have
    serverAlerts.forEach(serverAlert => {
      if (!alerts.find(a => a.id === serverAlert.id)) {
        addAlert({
          type: serverAlert.type,
          severity: serverAlert.severity,
          title: serverAlert.title,
          message: serverAlert.message,
          metadata: serverAlert.metadata,
        });
      }
    });
  } catch {
    // Ignore poll errors
  }
}

// Alert generators for common scenarios
export const AlertGenerators = {
  circuitApproved: (circuitId: string, circuitType: string) => addAlert({
    type: 'circuit',
    severity: 'info',
    title: 'Circuit Approved',
    message: `${circuitType} circuit ${circuitId.slice(0, 8)}... has been approved`,
    metadata: { circuitId, circuitType },
  }),
  
  circuitDeployed: (circuitId: string, circuitType: string) => addAlert({
    type: 'circuit',
    severity: 'info',
    title: 'Circuit Deployed',
    message: `${circuitType} circuit ${circuitId.slice(0, 8)}... is now running`,
    metadata: { circuitId, circuitType },
  }),
  
  circuitFailed: (circuitId: string, error: string) => addAlert({
    type: 'circuit',
    severity: 'critical',
    title: 'Circuit Failed',
    message: `Circuit ${circuitId.slice(0, 8)}... failed: ${error}`,
    metadata: { circuitId, error },
  }),
  
  nodeOffline: (nodeId: string, nodeType: string) => addAlert({
    type: 'node',
    severity: 'critical',
    title: 'Node Offline',
    message: `${nodeType} node ${nodeId.slice(0, 8)}... is offline`,
    metadata: { nodeId, nodeType },
  }),
  
  nodeDegraded: (nodeId: string, reason: string) => addAlert({
    type: 'node',
    severity: 'warning',
    title: 'Node Degraded',
    message: `Node ${nodeId.slice(0, 8)}... is degraded: ${reason}`,
    metadata: { nodeId, reason },
  }),
  
  versionDrift: (nodeId: string, current: string, expected: string) => addAlert({
    type: 'version',
    severity: 'warning',
    title: 'Version Drift Detected',
    message: `Node ${nodeId.slice(0, 8)}... running ${current}, expected ${expected}`,
    metadata: { nodeId, currentVersion: current, expectedVersion: expected },
  }),
  
  systemMessage: (title: string, message: string, severity: AlertSeverity = 'info') => addAlert({
    type: 'system',
    severity,
    title,
    message,
  }),
};

// Initialize on load
loadAlerts();

// Export types for consumers
export type { AlertFilter as AlertFilterType };
